import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Table,
  Container,
  Row,
  Col,
  Spinner,
  Form,
  Badge,
} from "react-bootstrap";
import { toast } from "sonner";
import axios from "axios";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { formatDateTime } from "../utils/dateUtils";

const alerpApiUrl = process.env.REACT_APP_ALERP_API_URL;
const alerpApiKey = process.env.REACT_APP_ALERP_API_KEY;

export default function Supplier() {
  const [searchTerm, setSearchTerm] = useState("");

  const {
    data: suppliers = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["alerpSuppliers"],
    queryFn: async () => {
      const res = await axios.get(`${alerpApiUrl}/challan_suppliers`, {
        headers: { "X-API-Key": alerpApiKey },
      });
      return res.data.data;
    },
    onError: (err) => {
      toast.error(`Failed to fetch suppliers from ALERP: ${err.message}`);
    },
    enabled: !!(alerpApiUrl && alerpApiKey),
  });

  const filteredSuppliers = useMemo(() => {
    const includesTerm = (value, term) => {
      if (value === null || value === undefined) return false;
      // Primitive
      if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        return String(value).toLowerCase().includes(term);
      }
      // Date object
      if (value instanceof Date && !isNaN(value)) {
        return value.toISOString().toLowerCase().includes(term);
      }
      // Array
      if (Array.isArray(value)) {
        return value.some((item) => includesTerm(item, term));
      }
      // Object
      if (typeof value === "object") {
        return Object.values(value).some((nested) => includesTerm(nested, term));
      }
      return false;
    };

    if (!suppliers || suppliers.length === 0) return [];
    if (!searchTerm.trim()) return suppliers;

    const term = searchTerm.trim().toLowerCase();
    return suppliers.filter((s) => includesTerm(s, term));
  }, [searchTerm, suppliers]);


  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Navbar />
      <div className="d-flex" style={{ flex: 1, overflow: "hidden" }}>
        <Sidebar />
        <Container
          fluid
          className="p-4"
          style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
        >
          {/* Header Row */}
          <Row className="mb-4  align-items-center">
            <Col>
              <div className="d-flex align-items-center">
                <h4 className="fw-bold m-0">Suppliers</h4>
                <Badge bg="secondary" className="ms-3">
                  Source: ALERP
                </Badge>
              </div>
            </Col>
          </Row>

          {/* Search Box */}
          <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
            <div style={{ maxWidth: "300px" }} className="flex-grow-1">
              <Form.Control
                type="text"
                placeholder="Search supplier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Custom styles for responsive table */}
          <style>
            {`
              .responsive-table {
                width: 100%;
                border-collapse: collapse;
              }

              .responsive-table th,
              .responsive-table td {
                padding: 8px;
                font-size: 14px;
                white-space: normal; /* Allow text wrapping */
                word-break: break-word; /* Break long words */
                min-width: 120px; /* Minimum width for columns */
              }

              .responsive-table th {
                position: sticky;
                top: 0;
                background-color: #e9ecef;
                z-index: 1;
              }

              .table-container {
                height: calc(100vh - 200px); /* Adjust based on navbar/sidebar height */
                overflow-y: auto;
                overflow-x: auto;
                -webkit-overflow-scrolling: touch; /* Smooth scrolling on mobile */
              }

              @media (max-width: 768px) {
                .responsive-table th,
                .responsive-table td {
                  font-size: 12px;
                  padding: 6px;
                  min-width: 100px;
                }

                .responsive-table {
                  display: block;
                }

                .responsive-table thead {
                z-index: -1;
                  display: none; /* Hide headers on small screens */
                }

                .responsive-table tbody,
                .responsive-table tr {
                  display: block;
                  width: 100%;
                }

                .responsive-table td {
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  border: 1px solid #dee2e6;
                  padding: 8px;
                  position: relative;
                  text-align: right;
                  width: 100%;
                }

                .responsive-table td::before {
                  content: attr(data-label);
                  font-weight: bold;
                  text-align: left;
                  flex: 1;
                  white-space: nowrap;
                }

                .responsive-table td > div {
                  flex: 2;
                  text-align: right;
                }
              }

              @media (max-width: 576px) {
                .responsive-table td {
                  font-size: 10px;
                  padding: 4px;
                }

                .table-container {
                  height: calc(100vh - 180px); /* Adjust for smaller screens */
                }
              }
            `}
          </style>

          {/* Table Container */}
          <div className="table-container">
            <Table bordered hover className="responsive-table">
              <thead className="table-secondary  sticky-top">
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Type</th>
                  <th className="text-nowrap">HO Address</th>
                  <th className="text-nowrap">Local Address</th>
                  <th className="text-nowrap">Contact Person</th>
                  <th className="text-nowrap">Accounts Person</th>
                  <th className="text-nowrap">Created At</th>
                  <th className="text-nowrap">Updated At</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="10" className="text-center p-5">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-2 mb-0">Loading Suppliers...</p>
                    </td>
                  </tr>
                ) : isError ? (
                  <tr>
                    <td colSpan="10" className="text-center text-danger p-5">
                      <h5>Error Fetching Data</h5>
                      <p>{error.message}</p>
                    </td>
                  </tr>
                ) : filteredSuppliers.length > 0 ? (
                  filteredSuppliers.map((s) => (
                    <tr key={s.supplier_code}>
                      <td className="text-nowrap" data-label="Code">SUP{s.supplier_code}</td>
                      <td className="text-nowrap" data-label="Name">{s.supplier_name || "-"}</td>
                      <td className="text-nowrap" data-label="Email">{s.supplier_email || "-"}</td>
                      <td className="text-nowrap" data-label="Type">
                        {Array.isArray(s.supplier_type) ? (
                          <div>
                            {s.supplier_type.length > 0 ? (
                              s.supplier_type.map((t, idx) => (
                                <div key={idx} className="text-nowrap">
                                  {String(t || "-")}
                                </div>
                              ))
                            ) : (
                              "-"
                            )}
                          </div>
                        ) : (
                          s.supplier_type || "-"
                        )}
                      </td>
                      <td className="text-nowrap" data-label="HO Address">{s.ho_address || "-"}</td>
                      <td className="text-nowrap" data-label="Local Address">{s.local_address || "-" }</td>
                      <td className="text-nowrap" data-label="Contact Person">
                        {s.contact_person_name && (
                          <div>
                            <strong>Name:</strong> {s.contact_person_name}
                          </div>
                        )}
                        {s.contact_person_number && (
                          <div>
                            <strong>Ph:</strong> {s.contact_person_number}
                          </div>
                        )}
                        {s.contact_person_email && (
                          <div>
                            <strong>Email:</strong> {s.contact_person_email}
                          </div>
                        )}
                        {!s.contact_person_name &&
                          !s.contact_person_number &&
                          !s.contact_person_email &&
                          "-"}
                      </td>
                      <td data-label="Accounts Person">
                        {s.accounts_person_name && (
                          <div>
                            <strong>Name:</strong> {s.accounts_person_name}
                          </div>
                        )}
                        {s.accounts_person_number && (
                          <div>
                            <strong>Ph:</strong> {s.accounts_person_number}
                          </div>
                        )}
                        {s.accounts_person_email && (
                          <div>
                            <strong>Email:</strong> {s.accounts_person_email}
                          </div>
                        )}
                        {!s.accounts_person_name &&
                          !s.accounts_person_number &&
                          !s.accounts_person_email &&
                          "-"}
                      </td>
                      <td className="text-nowrap" data-label="Created At">
                        {formatDateTime(s.created_at)}
                      </td>
                      <td className="text-nowrap" data-label="Updated At">
                        {formatDateTime(s.updated_at)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center p-5">
                      <h5>No Suppliers Found</h5>
                      {searchTerm && (
                        <p>
                          Your search for "{searchTerm}" did not match any suppliers.
                        </p>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Container>
      </div>
    </div>
  );
}
